var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { relationBehavior } from './relations';
import { tokenize } from './tokenizer';
/** Diagnostic translation keys produced by the parser. */
export var TSL_DIAGNOSTIC_KEYS = {
    unterminatedQuote: 'tsl.diagnostic.unterminatedQuote',
    missingValue: 'tsl.diagnostic.missingValue',
    missingSecondValue: 'tsl.diagnostic.missingSecondValue',
    unknownPseudoValue: 'tsl.diagnostic.unknownPseudoValue',
    invalidNumberValue: 'tsl.diagnostic.invalidNumberValue',
    invalidDateValue: 'tsl.diagnostic.invalidDateValue',
    invalidBooleanValue: 'tsl.diagnostic.invalidBooleanValue',
    cannotNegate: 'tsl.diagnostic.cannotNegate',
    unexpectedSymbol: 'tsl.diagnostic.unexpectedSymbol',
    assumedFulltext: 'tsl.diagnostic.assumedFulltext',
    duplicateScope: 'tsl.diagnostic.duplicateScope',
    unmatchedOpenParen: 'tsl.diagnostic.unmatchedOpenParen',
    unmatchedCloseParen: 'tsl.diagnostic.unmatchedCloseParen',
    tooManyCombinations: 'tsl.diagnostic.tooManyCombinations',
    unknownCodebookValue: 'tsl.diagnostic.unknownCodebookValue',
    unrepresentableDisjunction: 'tsl.diagnostic.unrepresentableDisjunction',
};
var NUMBER_PATTERN = /^-?\d+([.,]\d+)?$/;
var DATE_PATTERNS = [
    /^\d{1,2}\.\d{1,2}\.\d{4}$/, // 31.12.2020
    /^\d{4}-\d{2}-\d{2}$/, // 2020-12-31
    /^\d{4}$/, // 2020
];
/**
 * Safety cap on the number of OR-groups produced by distributing nested
 * `(a or b) and (c or d) …` groups to disjunctive normal form. Realistic
 * queries stay far below it; a query that would exceed it is truncated and a
 * `tooManyCombinations` diagnostic is raised.
 */
var MAX_DNF_GROUPS = 256;
var pushDiagnostic = function (state, severity, messageKey, span, params) {
    state.diagnostics.push({ severity: severity, messageKey: messageKey, span: span, params: params });
};
var isSymbol = function (token, value) {
    return token !== undefined && token.type === 'symbol' && token.value === value;
};
/**
 * CZNA-217: under `fieldNeedsOperator`, whether the field label ending before `next`
 * is followed by something that makes it a field — an operator symbol, an operator word
 * the `isOperator` check accepts, or the end of the input after a space.
 */
var followedByOperator = function (state, next, isOperator) {
    var _a, _b;
    if (!state.registry.fieldNeedsOperator) {
        return true;
    }
    var token = state.tokens[next];
    if (!token) {
        var end = (_b = (_a = state.tokens[next - 1]) === null || _a === void 0 ? void 0 : _a.span.end) !== null && _b !== void 0 ? _b : 0;
        return /^\s/.test(state.input.slice(end));
    }
    if (token.type === 'symbol') {
        return token.value !== '(' && token.value !== ')';
    }
    return isOperator(next);
};
/** CZNA-217: {@link TslRegistry.matchField} gated by {@link followedByOperator}. */
var matchFieldAt = function (state, index, options) {
    var match = state.registry.matchField(state.tokens, index, options);
    if (!match) {
        return undefined;
    }
    return followedByOperator(state, index + match.consumed, function (next) {
        return state.registry.matchOperator(match.entry, state.tokens, next) !== undefined;
    })
        ? match
        : undefined;
};
/** CZNA-217: {@link TslRegistry.matchPseudoField} gated by {@link followedByOperator}. */
var matchPseudoFieldAt = function (state, index) {
    var match = state.registry.matchPseudoField(state.tokens, index);
    if (!match) {
        return undefined;
    }
    return followedByOperator(state, index + match.consumed, function (next) {
        return state.registry.isEqualityToken(state.tokens[next]);
    })
        ? match
        : undefined;
};
/** True for tokens that cannot start a value (they open the next clause). */
var startsNextClause = function (state, index) {
    var token = state.tokens[index];
    if (!token) {
        return false;
    }
    if (token.type === 'quoted') {
        return false;
    }
    if (token.type === 'symbol') {
        // Parentheses open/close a group; other symbols are operators (not clause
        // starts) and are handled by the value/operator readers.
        return token.value === '(' || token.value === ')';
    }
    if (state.registry.matchConnector(token)) {
        return true;
    }
    // CZNA-217: a dynamic field (a MARC address) does not end a value — `100d je 1900`
    // must keep `1900` as the value instead of reading it as field 190, subfield 0.
    return (matchFieldAt(state, index, { dynamic: false }) !== undefined ||
        matchPseudoFieldAt(state, index) !== undefined);
};
/** Reads a single value token (word or quoted). Returns undefined when absent. */
var readValueToken = function (state, index, field) {
    var token = state.tokens[index];
    if (!token) {
        return undefined;
    }
    if (token.type === 'symbol') {
        return undefined;
    }
    if (token.type === 'word' && startsNextClause(state, index)) {
        // Boolean values collide with connector words (Czech `ne` = NOT); for
        // boolean fields a recognizable boolean word is always a value.
        var isBooleanValue = (field === null || field === void 0 ? void 0 : field.dataType) === 'BOOLEAN' &&
            state.registry.parseBooleanWord(token.value) !== undefined;
        if (!isBooleanValue) {
            return undefined;
        }
    }
    return token;
};
var validateValue = function (state, field, token) {
    var rawValue = token.value;
    if (token.unterminated) {
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.unterminatedQuote, token.span);
    }
    switch (field.dataType) {
        case 'NUMBER':
            if (!NUMBER_PATTERN.test(rawValue)) {
                pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.invalidNumberValue, token.span, {
                    value: rawValue,
                    field: field.label,
                });
            }
            return rawValue;
        case 'DATE':
            if (!DATE_PATTERNS.some(function (pattern) { return pattern.test(rawValue); })) {
                pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.invalidDateValue, token.span, {
                    value: rawValue,
                    field: field.label,
                });
            }
            return rawValue;
        case 'BOOLEAN': {
            var parsed = state.registry.parseBooleanWord(rawValue);
            if (!parsed) {
                pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.invalidBooleanValue, token.span, {
                    value: rawValue,
                    field: field.label,
                });
                return rawValue;
            }
            return parsed;
        }
        default:
            return rawValue;
    }
};
/**
 * Parses one condition clause starting at the matched field. Returns the clause
 * or undefined when the clause is incomplete (missing its first value).
 */
var parseConditionClause = function (state, field, fieldConsumed) {
    var _a, _b;
    var startSpan = state.tokens[state.index].span.start;
    var cursor = state.index + fieldConsumed;
    var negation = state.pendingNot;
    state.pendingNot = undefined;
    var relation = field.defaultRelation;
    var hasExplicitOperator = false;
    var colonToken = state.tokens[cursor];
    if (colonToken && colonToken.type === 'symbol' && colonToken.value === ':') {
        cursor += 1;
        hasExplicitOperator = true;
    }
    else {
        var operatorMatch = state.registry.matchOperator(field, state.tokens, cursor);
        if (operatorMatch) {
            relation = operatorMatch.entry.code;
            cursor += operatorMatch.consumed;
            hasExplicitOperator = true;
        }
    }
    if (negation && state.registry.negation === 'clause') {
        // CZNA-217: the backend negates the whole condition; the relation stays as written.
    }
    else if (negation) {
        var negated = relationBehavior(relation).negatedCode;
        if (negated && field.relationSet.has(negated)) {
            relation = negated;
        }
        else {
            pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.cannotNegate, negation.span, {
                field: field.label,
            });
        }
    }
    var arity = relationBehavior(relation).arity;
    var clause = __assign(__assign({ kind: 'condition', fieldId: field.id, fieldLabel: field.label, relation: relation, span: { start: startSpan, end: (_b = (_a = state.tokens[cursor - 1]) === null || _a === void 0 ? void 0 : _a.span.end) !== null && _b !== void 0 ? _b : startSpan } }, (field.key !== undefined ? { fieldKey: field.key } : {})), (negation && state.registry.negation === 'clause' ? { negated: true } : {}));
    if (arity === 0) {
        state.index = cursor;
        state.expectation = { type: 'clauseStart' };
        return clause;
    }
    var valueToken = readValueToken(state, cursor, field);
    if (!valueToken) {
        state.index = cursor;
        state.expectation = hasExplicitOperator
            ? { type: 'value', field: field, relation: relation }
            : { type: 'operatorOrValue', field: field };
        pushDiagnostic(state, 'error', TSL_DIAGNOSTIC_KEYS.missingValue, clause.span, {
            field: field.label,
        });
        return undefined;
    }
    clause.value = validateValue(state, field, valueToken);
    clause.span.end = valueToken.span.end;
    cursor += 1;
    // Range handling: explicit BETWEEN relation or `value..value2` upgrade.
    var behavior = relationBehavior(relation);
    var nextToken = state.tokens[cursor];
    var isRangeSymbol = nextToken && nextToken.type === 'symbol' && nextToken.value === '..';
    var isRangeSeparatorWord = behavior.arity === 2 &&
        nextToken !== undefined &&
        nextToken.type === 'word' &&
        state.registry.betweenSeparators.has(nextToken.value.toLowerCase().trim());
    var betweenUpgrade = behavior.arity !== 2 && isRangeSymbol
        ? field.relations.find(function (candidate) { return candidate.behavior.arity === 2; })
        : undefined;
    if (behavior.arity === 2 || betweenUpgrade) {
        if (betweenUpgrade) {
            relation = betweenUpgrade.code;
            clause.relation = relation;
        }
        if (isRangeSymbol || isRangeSeparatorWord) {
            cursor += 1;
        }
        var value2Token = readValueToken(state, cursor, field);
        if (!value2Token) {
            state.index = cursor;
            state.expectation = { type: 'value2', field: field, relation: relation };
            pushDiagnostic(state, 'error', TSL_DIAGNOSTIC_KEYS.missingSecondValue, clause.span, {
                field: field.label,
            });
            return clause;
        }
        clause.value2 = validateValue(state, field, value2Token);
        clause.span.end = value2Token.span.end;
        cursor += 1;
    }
    state.index = cursor;
    state.expectation = { type: 'clauseStart' };
    return clause;
};
/**
 * Parses one pseudo-field (scope) clause. Returns the clause or undefined when
 * its value is missing/unknown.
 */
var parsePseudoClause = function (state, pseudo, fieldConsumed) {
    var _a, _b, _c, _d;
    var startSpan = state.tokens[state.index].span.start;
    var cursor = state.index + fieldConsumed;
    // CZNA-217: under clause negation a pseudo clause is a condition like any other.
    var negated = state.pendingNot !== undefined && state.registry.negation === 'clause';
    if (state.pendingNot && !negated) {
        // Pseudo (scope) clauses compile into a global scope selection, which has
        // no negated form.
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.cannotNegate, state.pendingNot.span, {
            field: pseudo.label,
        });
    }
    state.pendingNot = undefined;
    if (state.registry.isEqualityToken(state.tokens[cursor])) {
        cursor += 1;
    }
    var valueMatch = state.registry.matchPseudoValue(pseudo, state.tokens, cursor);
    if (!valueMatch) {
        var valueToken = readValueToken(state, cursor);
        state.index = valueToken ? cursor + 1 : cursor;
        state.expectation = { type: 'pseudoValue', pseudo: pseudo };
        pushDiagnostic(state, valueToken ? 'warning' : 'error', valueToken
            ? TSL_DIAGNOSTIC_KEYS.unknownPseudoValue
            : TSL_DIAGNOSTIC_KEYS.missingValue, {
            start: startSpan,
            end: (_c = (_a = valueToken === null || valueToken === void 0 ? void 0 : valueToken.span.end) !== null && _a !== void 0 ? _a : (_b = state.tokens[cursor - 1]) === null || _b === void 0 ? void 0 : _b.span.end) !== null && _c !== void 0 ? _c : startSpan,
        }, { field: pseudo.label, value: (_d = valueToken === null || valueToken === void 0 ? void 0 : valueToken.value) !== null && _d !== void 0 ? _d : '' });
        return undefined;
    }
    var endToken = state.tokens[cursor + valueMatch.consumed - 1];
    state.index = cursor + valueMatch.consumed;
    state.expectation = { type: 'clauseStart' };
    return __assign({ kind: 'pseudo', pseudoKey: pseudo.key, valueId: valueMatch.entry.id, valueLabel: valueMatch.entry.label, span: { start: startSpan, end: endToken.span.end } }, (negated ? { negated: true } : {}));
};
/** Builds a fulltext clause from a single bare token and advances the cursor. */
var parseFulltextClause = function (state, token) {
    if (state.pendingNot) {
        // Bare terms compile into the smart query, which has no negated form.
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.cannotNegate, state.pendingNot.span, {
            field: '',
        });
    }
    state.pendingNot = undefined;
    if (token.unterminated) {
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.unterminatedQuote, token.span);
    }
    var text = token.type === 'quoted' ? "\"".concat(token.value, "\"") : token.value;
    state.index += 1;
    state.expectation = { type: 'clauseStart' };
    return { kind: 'fulltext', text: text, span: __assign({}, token.span) };
};
/** True when the node is a bare-terms (fulltext) leaf. */
var isFulltextNode = function (node) { var _a; return node.type === 'clause' && ((_a = node.clause) === null || _a === void 0 ? void 0 : _a.kind) === 'fulltext'; };
/**
 * Parses a parenthesized sub-expression, or a single clause otherwise. Returns
 * undefined when nothing parseable is at the cursor (an incomplete clause or a
 * stray operator symbol) — the caller keeps making progress regardless.
 */
var parsePrimary = function (state) {
    var token = state.tokens[state.index];
    if (!token) {
        return undefined;
    }
    if (isSymbol(token, '(')) {
        if (state.pendingNot) {
            // A group cannot be negated in the OR-of-ANDs model; only relations can.
            pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.cannotNegate, state.pendingNot.span, {
                field: '',
            });
            state.pendingNot = undefined;
        }
        state.index += 1;
        state.expectation = { type: 'clauseStart' };
        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion: parsePrimary -> parseOrExpr -> parseAndExpr -> parsePrimary; one of the three has to come first
        var inner = parseOrExpr(state);
        if (isSymbol(state.tokens[state.index], ')')) {
            state.index += 1;
            state.expectation = { type: 'clauseStart' };
        }
        else {
            pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.unmatchedOpenParen, token.span);
        }
        return inner;
    }
    if (token.type === 'symbol') {
        // A stray ')' is handled by the caller loop; any other symbol here is an
        // operator with no field in front of it.
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.unexpectedSymbol, token.span, {
            symbol: token.value,
        });
        state.index += 1;
        return undefined;
    }
    var pseudoMatch = matchPseudoFieldAt(state, state.index);
    if (pseudoMatch) {
        return { type: 'clause', clause: parsePseudoClause(state, pseudoMatch.entry, pseudoMatch.consumed) };
    }
    var fieldMatch = matchFieldAt(state, state.index);
    if (fieldMatch) {
        return {
            type: 'clause',
            clause: parseConditionClause(state, fieldMatch.entry, fieldMatch.consumed),
        };
    }
    return { type: 'clause', clause: parseFulltextClause(state, token) };
};
/**
 * Parses an AND-sequence: adjacent operands (with an optional explicit `A`/AND
 * connector between them) combined with AND. Stops at end of input, a closing
 * paren, or an OR connector.
 */
var parseAndExpr = function (state) {
    var children = [];
    var hasNonFulltext = false;
    while (state.index < state.tokens.length) {
        var token = state.tokens[state.index];
        if (isSymbol(token, ')')) {
            break;
        }
        var connector = state.registry.matchConnector(token);
        if (connector === 'or') {
            break;
        }
        if (connector === 'and') {
            state.index += 1;
            state.expectation = { type: 'clauseStart' };
        }
        else if (connector === 'not') {
            state.pendingNot = token;
            state.index += 1;
        }
        else {
            var before = state.index;
            var node = parsePrimary(state);
            if (state.index === before) {
                // Defensive: never loop on a token parsePrimary refused to consume.
                state.index += 1;
            }
            else if (node) {
                // A bare term following a real condition is almost always a mistake (the
                // user forgot an operator); keep it as fulltext but flag it.
                if (isFulltextNode(node) && hasNonFulltext && node.type === 'clause' && node.clause) {
                    pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.assumedFulltext, node.clause.span, {
                        value: node.clause.kind === 'fulltext' ? node.clause.text : '',
                    });
                }
                if (!isFulltextNode(node)) {
                    hasNonFulltext = true;
                }
                children.push(node);
            }
        }
    }
    if (children.length === 1) {
        return children[0];
    }
    return { type: 'and', children: children };
};
/**
 * Parses an OR-expression: AND-sequences separated by OR connectors. This is
 * the grammar entry point (and the body of every parenthesized group).
 */
var parseOrExpr = function (state) {
    var branches = [parseAndExpr(state)];
    while (state.registry.matchConnector(state.tokens[state.index]) === 'or') {
        state.index += 1;
        state.expectation = { type: 'clauseStart' };
        branches.push(parseAndExpr(state));
    }
    if (branches.length === 1) {
        return branches[0];
    }
    return { type: 'or', children: branches };
};
/**
 * Distributes the boolean tree to disjunctive normal form: a list of OR-groups,
 * each an AND-ed list of clauses. Incomplete (clause-less) leaves are dropped.
 */
var toDnf = function (state, node) {
    switch (node.type) {
        case 'clause':
            return node.clause ? [[node.clause]] : [[]];
        case 'or':
            return node.children.flatMap(function (child) { return toDnf(state, child); });
        // `and` is the last member of the AstNode union; `default` keeps the switch total
        // (consistent-return, default-case) while the type stays narrowed to it.
        default: {
            var groups = [[]];
            for (var _i = 0, _a = node.children; _i < _a.length; _i++) {
                var child = _a[_i];
                var childGroups = toDnf(state, child);
                var next = [];
                for (var _b = 0, groups_1 = groups; _b < groups_1.length; _b++) {
                    var group = groups_1[_b];
                    for (var _c = 0, childGroups_1 = childGroups; _c < childGroups_1.length; _c++) {
                        var childGroup = childGroups_1[_c];
                        if (next.length >= MAX_DNF_GROUPS) {
                            state.truncated = true;
                            return next;
                        }
                        next.push(__spreadArray(__spreadArray([], group, true), childGroup, true));
                    }
                }
                groups = next;
            }
            return groups;
        }
    }
};
/** Merges consecutive fulltext clauses in a group into one clause. */
var mergeGroupFulltext = function (group) {
    var merged = [];
    for (var _i = 0, group_1 = group; _i < group_1.length; _i++) {
        var clause = group_1[_i];
        var last = merged[merged.length - 1];
        if (clause.kind === 'fulltext' && last && last.kind === 'fulltext') {
            merged[merged.length - 1] = {
                kind: 'fulltext',
                text: "".concat(last.text, " ").concat(clause.text),
                span: { start: last.span.start, end: clause.span.end },
            };
        }
        else {
            merged.push(clause);
        }
    }
    return merged;
};
/**
 * Turns the raw parser AST into the display tree exposed to the UI: drops
 * incomplete (clause-less) leaves, merges consecutive bare terms into one
 * fulltext clause, and collapses single-child AND/OR nodes. Returns undefined
 * when nothing recognizable is left.
 */
var cleanTree = function (node) {
    if (node.type === 'clause') {
        return node.clause ? { type: 'clause', clause: node.clause } : undefined;
    }
    var children = [];
    for (var _i = 0, _a = node.children; _i < _a.length; _i++) {
        var rawChild = _a[_i];
        var child = cleanTree(rawChild);
        if (child) {
            // Adjacent bare terms AND-ed together read as one fulltext phrase.
            var previous = children[children.length - 1];
            if (node.type === 'and' &&
                child.type === 'clause' &&
                child.clause.kind === 'fulltext' &&
                (previous === null || previous === void 0 ? void 0 : previous.type) === 'clause' &&
                previous.clause.kind === 'fulltext') {
                children[children.length - 1] = {
                    type: 'clause',
                    clause: {
                        kind: 'fulltext',
                        text: "".concat(previous.clause.text, " ").concat(child.clause.text),
                        span: { start: previous.clause.span.start, end: child.clause.span.end },
                    },
                };
            }
            else {
                children.push(child);
            }
        }
    }
    if (children.length === 0) {
        return undefined;
    }
    if (children.length === 1) {
        return children[0];
    }
    return { type: node.type, children: children };
};
/**
 * Parses a TSL query. Never throws — problems surface as diagnostics and the
 * parser always makes progress, so partially-typed queries parse too.
 *
/**
 * A stable signature of a group's global (fulltext + pseudo) clauses, used to
 * compare whether two OR-branches carry the same global part.
 */
var globalSignature = function (group) {
    return group
        .filter(function (clause) { return clause.kind !== 'condition'; })
        .map(function (clause) {
        return clause.kind === 'fulltext' ? "f:".concat(clause.text) : "p:".concat(clause.pseudoKey, "=").concat(clause.valueId);
    })
        .sort()
        .join('|');
};
/**
 * Detects OR-queries whose meaning the flat backend request shape (a linear
 * row chain plus one global smart query and one global scope) silently
 * changes, so the parser can warn instead of returning confidently-wrong
 * results. Two cases, both only relevant for a real disjunction (>1 group):
 *
 * 1. Fulltext / pseudo (scope) clauses are global: the smart query is appended
 *    as a single AND row that binds only to the last row-emitting group, and
 *    scope applies to the whole query. They are faithful only when they live
 *    exclusively in the last row-emitting group (e.g. `Autor:X a jan`). A bare
 *    term or scope in any other OR-branch (`hrad nebo Autor:X`) is lost.
 * 2. Same-field boundary collision: the backend merges the first (OR-tagged)
 *    row of a group into the previous row when they share a search field,
 *    stranding the rest of a multi-condition branch — so
 *    `Autor:Čapek nebo Autor:Hašek a Název:Hrad` runs as
 *    `(Čapek OR Hašek) AND Hrad` instead of `Čapek OR (Hašek AND Hrad)`.
 *    Single-condition same-field branches (`Autor:X nebo Autor:Y`) are the
 *    intended merge and stay faithful.
 */
var isUnrepresentableDisjunction = function (groups) {
    if (groups.length <= 1) {
        return false;
    }
    var conditionsOf = function (group) {
        return group.filter(function (clause) { return clause.kind === 'condition'; });
    };
    var hasGlobal = function (group) {
        return group.some(function (clause) { return clause.kind !== 'condition'; });
    };
    var lastRowGroupIndex = -1;
    groups.forEach(function (group, index) {
        if (conditionsOf(group).length > 0) {
            lastRowGroupIndex = index;
        }
    });
    if (lastRowGroupIndex === -1) {
        // Pure fulltext/pseudo disjunction: faithful only when every branch carries
        // the identical global part (which collapses to one smart query/scope).
        var first_1 = globalSignature(groups[0]);
        return groups.some(function (group) { return globalSignature(group) !== first_1; });
    }
    // Case 1: a global clause outside the single last row-emitting group is lost.
    if (groups.some(function (group, index) { return index !== lastRowGroupIndex && hasGlobal(group); })) {
        return true;
    }
    // Case 2: same-field boundary collision between consecutive row-emitting groups.
    var rowGroups = groups.map(conditionsOf).filter(function (conditions) { return conditions.length > 0; });
    for (var index = 1; index < rowGroups.length; index += 1) {
        var previous = rowGroups[index - 1];
        var current = rowGroups[index];
        var boundaryCollides = previous[previous.length - 1].fieldId === current[0].fieldId;
        if (boundaryCollides && (previous.length >= 2 || current.length >= 2)) {
            return true;
        }
    }
    return false;
};
/**
 * Parentheses group AND/OR sub-expressions; the tree is distributed to
 * disjunctive normal form (`groups`: OR of AND-ed clauses), the only shape the
 * combined-search backend understands. So `(a or b) and c` yields the two
 * groups `[a, c]` and `[b, c]`.
 */
export var parseTsl = function (input, registry) {
    var state = {
        input: input,
        tokens: tokenize(input),
        index: 0,
        diagnostics: [],
        pendingNot: undefined,
        expectation: { type: 'clauseStart' },
        registry: registry,
        truncated: false,
    };
    // Top-level OR-expressions separated by stray closing parens (which are
    // reported): `a) b` parses as `a and b`, never dropping the tail.
    var branches = [parseOrExpr(state)];
    while (state.index < state.tokens.length && isSymbol(state.tokens[state.index], ')')) {
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.unmatchedCloseParen, state.tokens[state.index].span);
        state.index += 1;
        state.expectation = { type: 'clauseStart' };
        if (state.index < state.tokens.length) {
            branches.push(parseOrExpr(state));
        }
    }
    if (state.pendingNot) {
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.cannotNegate, state.pendingNot.span, {
            field: '',
        });
    }
    var ast = branches.length === 1 ? branches[0] : { type: 'and', children: branches };
    var groups = toDnf(state, ast)
        .map(mergeGroupFulltext)
        .filter(function (group) { return group.length > 0; });
    // CZNA-217: a tree-taking backend never sees the DNF groups, so neither check applies.
    if (state.truncated && registry.checkDisjunction) {
        pushDiagnostic(state, 'warning', TSL_DIAGNOSTIC_KEYS.tooManyCombinations, undefined, {
            limit: String(MAX_DNF_GROUPS),
        });
    }
    if (registry.checkDisjunction && isUnrepresentableDisjunction(groups)) {
        pushDiagnostic(state, 'error', TSL_DIAGNOSTIC_KEYS.unrepresentableDisjunction, undefined, {});
    }
    var clauses = groups.flat();
    return {
        input: input,
        groups: groups,
        clauses: clauses,
        tree: cleanTree(ast),
        diagnostics: state.diagnostics,
        isEmpty: clauses.length === 0,
        expectation: state.expectation,
    };
};
